<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useActivitiesStore } from 'dashboard/stores/activities';
import { useDealsStore } from 'dashboard/stores/deals';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ActivityCard from 'dashboard/components-next/Activities/ActivityCard.vue';
import ActivityList from 'dashboard/components-next/Activities/ActivityList.vue';
import ActivityForm from 'dashboard/components-next/Activities/ActivityForm.vue';
import DealCard from 'dashboard/components-next/Deals/DealCard.vue';

const { t } = useI18n();
const router = useRouter();
const activitiesStore = useActivitiesStore();
const dealsStore = useDealsStore();

const showForm = ref(false);
const selectedActivity = ref(null);
const isLoading = ref(false);
const showAllActivities = ref(false);

const pendingActivities = ref([]);
const openDeals = ref([]);
const allActivities = ref([]);
const isLoadingAll = ref(false);

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const endOfWeek = () => {
  const d = startOfToday();
  d.setDate(d.getDate() + 7);
  return d;
};

const isToday = dueDate => {
  if (!dueDate) return false;
  const date = new Date(dueDate * 1000);
  return date >= startOfToday() && date <= endOfToday();
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || status !== 'pending') return false;
  const date = new Date(dueDate * 1000);
  return date < startOfToday();
};

const isThisWeek = expectedCloseDate => {
  if (!expectedCloseDate) return false;
  const date = new Date(expectedCloseDate);
  return date >= startOfToday() && date <= endOfWeek();
};

const todayActivities = computed(() =>
  pendingActivities.value.filter(a => isToday(a.dueDate))
);

const overdueActivities = computed(() =>
  pendingActivities.value.filter(a => isOverdue(a.dueDate, a.status))
);

const weeklyDeals = computed(() =>
  openDeals.value.filter(d => isThisWeek(d.expectedCloseDate))
);

const loadWorkbench = async () => {
  isLoading.value = true;
  try {
    const [activitiesResponse, dealsResponse] = await Promise.all([
      activitiesStore.get({ status: 'pending', page: 1 }),
      dealsStore.get({ status: 'open', page: 1 }),
    ]);
    pendingActivities.value =
      activitiesResponse || activitiesStore.getActivitiesList;
    openDeals.value = dealsResponse || dealsStore.getDealsList;
  } catch (error) {
    useAlert(error.message || t('ACTIVITIES.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const loadAllActivities = async () => {
  isLoadingAll.value = true;
  try {
    await activitiesStore.get({ page: 1 });
    allActivities.value = activitiesStore.getActivitiesList;
  } catch (error) {
    useAlert(error.message || t('ACTIVITIES.ERROR_MESSAGE'));
  } finally {
    isLoadingAll.value = false;
  }
};

const toggleAllActivities = () => {
  showAllActivities.value = !showAllActivities.value;
  if (showAllActivities.value && allActivities.value.length === 0) {
    loadAllActivities();
  }
};

const openCreateForm = () => {
  selectedActivity.value = null;
  showForm.value = true;
};

const openEditForm = activity => {
  selectedActivity.value = activity;
  showForm.value = true;
};

const handleSubmit = async activityData => {
  try {
    if (selectedActivity.value) {
      await activitiesStore.update({
        id: selectedActivity.value.id,
        ...activityData,
      });
      useAlert(t('ACTIVITIES.EDIT.SUCCESS_MESSAGE'));
    } else {
      await activitiesStore.create(activityData);
      useAlert(t('ACTIVITIES.CREATE.SUCCESS_MESSAGE'));
    }
    showForm.value = false;
    await loadWorkbench();
  } catch (error) {
    useAlert(error.message || t('ACTIVITIES.ERROR_MESSAGE'));
  }
};

const handleComplete = async activityId => {
  try {
    await activitiesStore.complete(activityId);
    useAlert(t('ACTIVITIES.COMPLETE.SUCCESS_MESSAGE'));
    await loadWorkbench();
  } catch (error) {
    useAlert(error.message || t('ACTIVITIES.ERROR_MESSAGE'));
  }
};

const handleDelete = async activity => {
  try {
    await activitiesStore.delete(activity.id);
    useAlert(t('ACTIVITIES.DELETE.SUCCESS_MESSAGE'));
    await loadWorkbench();
  } catch (error) {
    useAlert(error.message || t('ACTIVITIES.ERROR_MESSAGE'));
  }
};

const handleDealClick = deal => {
  router.push({ name: 'deals_detail', params: { dealId: deal.id } });
};

onMounted(loadWorkbench);
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-auto bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="flex items-center justify-between mx-auto max-w-5xl py-4">
        <h2 class="text-xl font-medium truncate text-n-slate-12">
          {{ t('ACTIVITIES.WORKBENCH.HEADER') }}
        </h2>
        <button
          class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-md transition-colors"
          @click="openCreateForm"
        >
          <i class="i-lucide-plus size-4" />
          {{ t('ACTIVITIES.CREATE.BUTTON_TEXT') }}
        </button>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6">
      <div class="mx-auto max-w-5xl py-4">
        <Spinner v-if="isLoading" />
        <div v-else class="flex flex-col gap-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              class="flex flex-col bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm rounded-lg p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <h3
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ t('ACTIVITIES.WORKBENCH.TODAY_TITLE') }}
                </h3>
                <span
                  class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700"
                >
                  {{ todayActivities.length }}
                </span>
              </div>
              <div
                v-if="todayActivities.length > 0"
                class="flex flex-col gap-2"
              >
                <ActivityCard
                  v-for="activity in todayActivities"
                  :key="activity.id"
                  :activity="activity"
                  @complete="handleComplete"
                  @edit="openEditForm"
                  @delete="handleDelete"
                />
              </div>
              <div
                v-else
                class="flex flex-col items-center justify-center py-8 text-center"
              >
                <span
                  class="i-lucide-check-circle-2 size-6 mb-2 text-slate-300 dark:text-slate-600"
                />
                <p class="text-xs text-slate-400 dark:text-slate-500">
                  {{ t('ACTIVITIES.WORKBENCH.TODAY_EMPTY') }}
                </p>
              </div>
            </div>

            <div
              class="flex flex-col bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm rounded-lg p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <h3
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ t('ACTIVITIES.WORKBENCH.OVERDUE_TITLE') }}
                </h3>
                <span
                  class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-medium rounded-full bg-red-50 text-red-700"
                >
                  {{ overdueActivities.length }}
                </span>
              </div>
              <div
                v-if="overdueActivities.length > 0"
                class="flex flex-col gap-2"
              >
                <ActivityCard
                  v-for="activity in overdueActivities"
                  :key="activity.id"
                  :activity="activity"
                  @complete="handleComplete"
                  @edit="openEditForm"
                  @delete="handleDelete"
                />
              </div>
              <div
                v-else
                class="flex flex-col items-center justify-center py-8 text-center"
              >
                <span
                  class="i-lucide-shield-check size-6 mb-2 text-slate-300 dark:text-slate-600"
                />
                <p class="text-xs text-slate-400 dark:text-slate-500">
                  {{ t('ACTIVITIES.WORKBENCH.OVERDUE_EMPTY') }}
                </p>
              </div>
            </div>

            <div
              class="flex flex-col bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm rounded-lg p-4"
            >
              <div class="flex items-center justify-between mb-3">
                <h3
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ t('ACTIVITIES.WORKBENCH.WEEKLY_DEALS_TITLE') }}
                </h3>
                <span
                  class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-medium rounded-full bg-green-50 text-green-700"
                >
                  {{ weeklyDeals.length }}
                </span>
              </div>
              <div v-if="weeklyDeals.length > 0" class="flex flex-col gap-2">
                <DealCard
                  v-for="deal in weeklyDeals"
                  :key="deal.id"
                  :deal="deal"
                  @click="handleDealClick"
                />
              </div>
              <div
                v-else
                class="flex flex-col items-center justify-center py-8 text-center"
              >
                <span
                  class="i-lucide-trending-up size-6 mb-2 text-slate-300 dark:text-slate-600"
                />
                <p class="text-xs text-slate-400 dark:text-slate-500">
                  {{ t('ACTIVITIES.WORKBENCH.WEEKLY_DEALS_EMPTY') }}
                </p>
              </div>
            </div>
          </div>

          <div
            class="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm rounded-lg p-4"
          >
            <button
              class="flex items-center gap-1 text-sm font-medium text-slate-700 dark:text-slate-300 w-full"
              @click="toggleAllActivities"
            >
              <i
                class="size-4"
                :class="
                  showAllActivities
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
              />
              {{ t('ACTIVITIES.WORKBENCH.ALL_ACTIVITIES_TITLE') }}
            </button>
            <div v-if="showAllActivities" class="mt-3">
              <Spinner v-if="isLoadingAll" />
              <ActivityList
                v-else
                :activities="allActivities"
                @complete="handleComplete"
                @edit="openEditForm"
                @delete="handleDelete"
              />
            </div>
          </div>
        </div>
      </div>
    </main>

    <ActivityForm
      v-if="showForm"
      :activity="selectedActivity"
      @submit="handleSubmit"
      @close="showForm = false"
    />
  </div>
</template>
